import { createFeatureSelector, createSelector } from '@ngrx/store';
import { rollupFeatureKey, RollupState } from './rollups.reducer';
import {
  IRollupGCLineItem,
  IRollupLineItems,
  IRollupLineItemTable,
  IRollupLineItemTableTotals,
  IRollupPrimeProject,
  IRollupPrimeProjectsTotals,
  IRollupProject,
  IRollupProjectTable,
  IRollupProjectTableGCTotals,
  IRollupProjectTableTotals,
  IRollupSubProjectsTotals,
  IRollupTag,
  IRollupTagTable,
  IRollupTagTotals,
} from './rollups.interface';
import {
  emptyGCLineItemTotals,
  emptyPrimeProjectTotals,
  emptySubProjectTotals,
  emptyTotals,
} from './rollups.constants';
import { PROJECT_STATUS_KEY } from '@app/pages/webapp/projects/projects.interface';

export const getRollupState = createFeatureSelector<RollupState>(rollupFeatureKey);

export const getAllProjects = createSelector(getRollupState, (state) => state.projects_list);
export const selectRollupPrimeProjects = createSelector(
  getRollupState,
  (state) => state.prime_projects_list,
);
export const selectRollupSubProjects = createSelector(getRollupState, (state) =>
  state.sub_projects_list.filter(
    (subproject) => subproject.project_status.key !== PROJECT_STATUS_KEY.DELETED,
  ),
);
export const getAllLineItems = createSelector(getRollupState, (state) => state.line_items_list);
export const getAllTags = createSelector(getRollupState, (state) => state.tags_list);

export const getRollupProjectsList = createSelector(getAllProjects, (projects) =>
  projects.map((project) => transformProjectTable(project)),
);

export const getRollupLineItemsList = createSelector(getAllLineItems, (lineItems) =>
  lineItems.map((bli) => transformRollupLineItemsTable(bli)),
);

export const getRollupTagsList = createSelector(getAllTags, (tags) =>
  tags.map((acc) => transformRollupTagsTable(acc)),
);

export const getRollupSelectedData = createSelector(
  getRollupState,
  (state) => state.selectedRollupData,
);
export const getRollupViewType = createSelector(
  getRollupState,
  (state) => state.selectedRollupViewType,
);

const selectIsLoading = createSelector(getRollupState, (state) => state.isLoading);
const selectIsFetching = createSelector(getRollupState, (state) => state.isFetching);

export const rollupsSelectors = {
  selectIsLoading,
  selectIsFetching,
};

const sumTotalProjects = (
  acc: IRollupProjectTableTotals,
  curr: IRollupProjectTableTotals,
): IRollupProjectTableTotals => {
  const data: IRollupProjectTableTotals = {
    current_budget: acc.current_budget + curr.current_budget,
    contracts: acc.contracts + curr.contracts,
    total_committed: acc.total_committed + curr.total_committed,
    actuals: acc.actuals + curr.actuals,
    forecasts_to_complete: acc.forecasts_to_complete + curr.forecasts_to_complete,
    forecast_modifications: acc.forecast_modifications + curr.forecast_modifications,
    total_invoiced: acc.total_invoiced + curr.total_invoiced,
    total_committed_vs_current_budget:
      acc.total_committed_vs_current_budget + curr.total_committed_vs_current_budget,
    complete: (acc?.complete ?? 0) + (curr?.complete ?? 0),
  };
  data.complete = calculatePercentage(data);
  return data;
};

const sumTotalGeneral = (acc, curr) => {
  const data = {
    current_budget: acc.current_budget + curr.current_budget,
    total_committed: acc.total_committed + curr.total_committed,
    actuals: acc.actuals + curr.actuals,
    total_committed_vs_current_budget:
      acc.total_committed_vs_current_budget + curr.total_committed_vs_current_budget,
    complete: (acc?.complete ?? 0) + (curr?.complete ?? 0),
  };
  data.complete = calculatePercentage(data);
  return data;
};

const sumTotalPrimeProjects = (acc, curr): Partial<IRollupPrimeProject> => {
  const data: Partial<IRollupPrimeProject> = {
    current_budget: acc.current_budget + curr.current_budget,
    prime_contract: acc.prime_contract + curr.prime_contract,
    anticipated_income: acc.anticipated_income + curr.anticipated_income,
    committed_costs: acc.committed_costs + curr.committed_costs,
    anticipated_costs: acc.anticipated_costs + curr.anticipated_costs,
    anticipated_fee: acc.anticipated_fee + curr.anticipated_fee,
    over_under: acc.over_under + curr.over_under,
    under_over: acc.under_over + curr.under_over,
  };
  return data;
};

const sumTotalSubProjects = (acc, curr): Partial<IRollupSubProjectsTotals> => {
  const data: Partial<IRollupSubProjectsTotals> = {
    original_subcontract: acc.original_subcontract + curr.original_subcontract,
    approved_scos: acc.approved_scos + curr.approved_scos,
    pending_scos: acc.pending_scos + curr.pending_scos,
    current_subcontract: acc.current_subcontract + curr.current_subcontract,
    total_invoiced: acc.total_invoiced + curr.total_invoiced,
    paid_invoices: acc.paid_invoices + curr.paid_invoices,
    left_to_invoice: acc.left_to_invoice + curr.left_to_invoice,
  };
  return data;
};

export const getRollupProjectsListTotal = createSelector(getRollupState, (state) => {
  const totals = state.projects_list
    .map((project): IRollupProjectTableTotals => {
      return {
        current_budget: project.cash_flow.current_budget,
        contracts: project.cash_flow.contracts,
        total_committed: project.cash_flow.total_committed,
        actuals: project.cash_flow.actuals,
        forecasts_to_complete: project.cash_flow.forecasts_to_complete,
        forecast_modifications: project.cash_flow.forecast_modifications,
        total_invoiced: project.cash_flow.total_invoiced,
        total_committed_vs_current_budget:
          project.cash_flow.current_budget - project.cash_flow.total_committed,
        complete: calculatePercentage(project.cash_flow),
      };
    })
    .reduce(sumTotalProjects, emptyTotals);
  totals.title = 'Totals';
  totals.total_projects =
    state.projects_list.length === 1
      ? `${state.projects_list.length} Project`
      : `${state.projects_list.length} Projects`;
  return totals;
});

export const selectRollupPrimeProjectsTotal = createSelector(getRollupState, (state) => {
  const totals: IRollupPrimeProjectsTotals = state.prime_projects_list.reduce(
    sumTotalPrimeProjects,
    emptyPrimeProjectTotals,
  );
  totals.title = 'Totals';
  totals.total_projects =
    state.prime_projects_list.length === 1
      ? `${state.prime_projects_list.length} Project`
      : `${state.prime_projects_list.length} Projects`;

  return totals;
});

export const selectRollupSubProjectsTotal = createSelector(getRollupState, (state) => {
  const totals: IRollupSubProjectsTotals = state.sub_projects_list.reduce(
    sumTotalSubProjects,
    emptySubProjectTotals,
  );
  totals.title = 'Totals';
  totals.total_projects =
    state.sub_projects_list.length === 1
      ? `${state.sub_projects_list.length} Project`
      : `${state.sub_projects_list.length} Projects`;

  return totals;
});

export const getRollupLineItemsListTotal = createSelector(getRollupState, (state) => {
  const totals = state.line_items_list
    .map((bli): IRollupLineItemTableTotals => {
      return {
        current_budget: bli.cash_flow.current_budget,
        total_committed: bli.cash_flow.total_committed,
        total_committed_vs_current_budget:
          bli.cash_flow.current_budget - bli.cash_flow.total_committed,
        actuals: bli.cash_flow.actuals,
        complete: calculatePercentage(bli.cash_flow),
      };
    })
    .reduce(sumTotalGeneral, emptyTotals);
  totals.title = 'Totals';
  return totals;
});

// todo: work on types & data as backend is ready
export const getRollupGCLineItemsListTotal = createSelector(getRollupState, (state) => {
  const totals: IRollupProjectTableGCTotals = (state.line_items_list as IRollupGCLineItem[]).reduce(
    (acc, curr) => {
      return {
        current_budget: curr.current_budget + acc.current_budget,
        prime_contract: curr.prime_contract + acc.prime_contract,
        anticipated_income: curr.anticipated_income + acc.anticipated_income,
        over_under: curr.over_under + acc.over_under,
        committed_costs: curr.committed_costs + acc.committed_costs,
        anticipated_costs: curr.anticipated_costs + acc.anticipated_costs,
        under_over: curr.under_over + acc.under_over,
        anticipated_fee: curr.anticipated_fee + acc.anticipated_fee,
      };
    },
    emptyGCLineItemTotals,
  );
  totals.title = 'Totals';
  return totals;
});

export const getRollupFilters = createSelector(getRollupState, (state) => state.filters);

const sumTagsTotal = (acc, curr): IRollupTagTotals => {
  const data: IRollupTagTotals = {
    total_committed: acc.total_committed + curr.total_committed,
    actuals: acc.actuals + curr.actuals,
    actuals_vs_total_committed: acc.actuals_vs_total_committed + curr.actuals_vs_total_committed,
    complete: (acc?.complete ?? 0) + (curr?.complete ?? 0),
  };
  data.complete = calculatePercentage(acc);
  return data;
};
export const getRollupTagsListTotal = createSelector(getRollupState, (state) => {
  const totals = state.tags_list
    .map((tag: IRollupTag): IRollupTagTotals => {
      return {
        total_committed: tag.cash_flow.total_committed,
        actuals: tag.cash_flow.actuals,
        actuals_vs_total_committed: tag.cash_flow.total_committed - tag.cash_flow.actuals,
        complete: calculatePercentage(tag.cash_flow),
      };
    })
    .reduce(sumTagsTotal, emptyTotals);
  totals.title = 'Totals';
  return totals;
});

export const getRollupShowZeroDollarLines = createSelector(getRollupState, (state) => {
  return state.showZeroDollarLines;
});

const transformProjectTable = (project: IRollupProject): IRollupProjectTable => {
  const { cash_flow, ...rest } = project;
  return {
    ...rest,
    project_status: project.project_status,
    current_budget: cash_flow.current_budget,
    contracts: cash_flow.contracts,
    total_committed: cash_flow.total_committed,
    actuals: cash_flow.actuals,
    forecasts_to_complete: cash_flow.forecasts_to_complete,
    total_invoiced: cash_flow.total_invoiced,
    forecast_modifications: cash_flow.forecast_modifications,
    complete: calculatePercentage(cash_flow),
    description: project.description,
  };
};

const transformRollupLineItemsTable = (lineItems: IRollupLineItems): IRollupLineItemTable => {
  const { cash_flow, ...rest } = lineItems;
  return {
    ...rest,
    current_budget: cash_flow.current_budget,
    actuals: cash_flow.actuals,
    total_committed: cash_flow.total_committed,
    total_committed_vs_current_budget: cash_flow.current_budget - cash_flow.total_committed,
    complete: calculatePercentage(cash_flow),
  };
};

const transformRollupTagsTable = (tags: IRollupTag): IRollupTagTable => {
  const { cash_flow, ...rest } = tags;
  return {
    ...rest,
    actuals: cash_flow.actuals,
    total_committed: cash_flow.total_committed,
    actuals_vs_total_committed: cash_flow.total_committed - cash_flow.actuals,
    complete: calculatePercentage(cash_flow),
  };
};

const calculatePercentage = (data) => {
  if (data.actuals === 0 || data.total_committed === 0) {
    return 0;
  }
  const complete = data.actuals / data.total_committed;
  return Math.round(complete * 100);
};
